import React, { useEffect, useState } from 'react'
import getParamsFromUrl from '../../../utils/getParamsFromUrl'
import { BlockRenderer } from '@/siteBuilder/renderer/contentful/BlockRenderer'
import _ from 'lodash'
import { FlexGrid, Typography, StackView, Box } from '@telus-uds/components-web'
import useBBViewport from '@/hooks/useBBViewport'
import { StickyCard } from './Styles'
import { IDynamicProductProps } from '../DynamicProducts/DynamicProducts'
import { RowProps } from '../../core/Row/Row'
import { CardProps } from '../../core/Card/Card'
import { FormBuilderProps } from '../../core/FormBuilder/FormBuilder'

type DynamicCardProps = {
  fields: {
    heading: RowProps
    card: CardProps
    matchId: string
  }
}

export type FormWithParamsProps = {
  formHeading?: string
  formText?: string
  component?: IDynamicProductProps
  dynamicCards?: DynamicCardProps[]
  defaultDynamicCard?: DynamicCardProps
  form: FormBuilderProps
}

const FormWithUrlParams = ({
  formHeading,
  formText,
  component,
  dynamicCards,
  defaultDynamicCard,
  form,
}: FormWithParamsProps) => {
  const viewport = useBBViewport()
  const [urlParams, setUrlParams] = useState({})
  const [componentData, setComponentDataState] = useState({})
  const [dynamicCardContent, setDynamicCardContent] = useState(null)
  const [showDynamicCardContent, setShowDynamicCardContent] = useState(false)

  const findDynamicCardContent = (params) => {
    if (defaultDynamicCard && !component) {
      if (params.matchId && dynamicCards) {
        const card = dynamicCards.find((card) => card.fields.matchId === params.matchId)
        if (card) {
          setDynamicCardContent(card)
        } else {
          setDynamicCardContent(defaultDynamicCard)
        }
      } else {
        setDynamicCardContent(defaultDynamicCard)
      }

      setShowDynamicCardContent(true)
    }
  }

  const setComponentData = (state: string) => {
    if (!_.isEqual(state, componentData)) {
      setComponentDataState(state)
    }
  }

  const hideDynamicCardContent = () => setShowDynamicCardContent(false)

  useEffect(() => {
    const params = getParamsFromUrl()
    component ? setUrlParams(params) : setComponentDataState(params)
    findDynamicCardContent(params)
  }, [])

  return (
    <Box space={3}>
      <FlexGrid>
        {(formHeading || formText) && (
          <FlexGrid.Col>
            <StackView space={3}>
              {formHeading && (
                <Typography block variant={{ size: 'h1' }}>
                  {formHeading}
                </Typography>
              )}
              {formText && (
                <Typography block variant={{ size: 'large' }}>
                  {formText}
                </Typography>
              )}
            </StackView>
          </FlexGrid.Col>
        )}
        <FlexGrid.Row>
          <FlexGrid.Col md={showDynamicCardContent ? 8 : 12}>
            <div data-testid="form-with-params">
              {!_.isEmpty(urlParams) && component && (
                <BlockRenderer block={component} urlParams={urlParams} setComponentData={setComponentData} />
              )}
              {['md', 'lg', 'xl'].includes(viewport) && showDynamicCardContent && (
                <BlockRenderer block={dynamicCardContent?.fields?.heading} />
              )}
              {['xs', 'sm'].includes(viewport) && showDynamicCardContent && (
                <>
                  <BlockRenderer block={dynamicCardContent?.fields?.heading} />
                  <BlockRenderer block={dynamicCardContent?.fields?.card} />
                </>
              )}
              {!_.isEmpty(componentData) ? (
                <BlockRenderer
                  block={form}
                  extraData={componentData}
                  submitFormHandler={showDynamicCardContent && hideDynamicCardContent}
                />
              ) : (
                <BlockRenderer block={form} submitFormHandler={showDynamicCardContent && hideDynamicCardContent} />
              )}
            </div>
          </FlexGrid.Col>
          {['md', 'lg', 'xl'].includes(viewport) && showDynamicCardContent && (
            <FlexGrid.Col md={4}>
              <StickyCard data-testid="sticky-card">
                <BlockRenderer block={dynamicCardContent?.fields?.card} />
              </StickyCard>
            </FlexGrid.Col>
          )}
        </FlexGrid.Row>
      </FlexGrid>
    </Box>
  )
}

export default FormWithUrlParams
